import React, { useState, useEffect, useRef } from 'react';
import {
  Button, 
  CardActions, 
  CardContent,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Skeleton,
  useTheme
} from '@mui/material';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import {
  Edit as EditIcon,
  PostAdd as PostIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon
} from '@mui/icons-material';
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";

const TemplateCard = ({ 
  template, 
  isMobile = false, 
  onTemplateLoaded,
  loading = false 
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);
  const theme = useTheme();

  // Make images visible immediately (performance integration disabled)
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Handle image load events
  const handleImageLoad = () => {
    setImageLoaded(true);
    if (onTemplateLoaded) {
      onTemplateLoaded(template);
    }
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  // Enhanced card styling with better visual hierarchy
  const cardStyles = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    
    // Add subtle gradient border
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: theme.spacing(2),
      padding: '1px',
      background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      maskComposite: 'xor',
      pointerEvents: 'none',
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',
      borderColor: theme.palette.primary.light,
      
      '&::before': {
        opacity: 1,
      },
      
      '& .template-actions': {
        opacity: 1,
        transform: 'translateY(0)',
      },
      
      '& .template-image': {
        transform: 'scale(1.05)',
      },
      
      '& .template-badge': {
        transform: 'scale(1.1)',
      }
    },
    
    '&:focus-within': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    
    // Mobile touch optimizations
    ...(isMobile && {
      minHeight: '44px', // Touch target size
      '&:active': {
        transform: 'scale(0.98)',
      }
    })
  };

  if (loading) {
    return (
      <Card sx={cardStyles} ref={cardRef}>
        <Skeleton variant="rectangular" height={200} />
        <CardContent>
          <Skeleton variant="text" height={24} />
          <Skeleton variant="text" height={20} width="60%" />
        </CardContent>
        <CardActions>
          <Skeleton variant="rectangular" width={60} height={32} />
          <Skeleton variant="rectangular" width={80} height={32} />
        </CardActions>
      </Card>
    );
  }

  return (
    <Card 
      sx={cardStyles} 
      ref={cardRef}
      data-template-id={template.id}
      data-testid="template-card"
      role="article"
      aria-labelledby={`template-title-${template.id}`}
      aria-describedby={`template-desc-${template.id}`}
      tabIndex={0}
      onKeyDown={(e) => {
        // Enhanced keyboard navigation
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          // Focus on first action button
          const firstAction = e.currentTarget.querySelector('button, a');
          if (firstAction) {
            firstAction.focus();
          }
        }
      }}
    >
      {/* Template Preview Image */}
      <Box sx={{ 
        position: 'relative', 
        height: 200, 
        overflow: 'hidden',
        backgroundColor: theme.palette.grey[100],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {isVisible ? (
          <>
            {!imageError ? (
              <CardMedia
                component="img"
                image={template.preview_img}
                alt={template.name}
                title={template.name}
                onLoad={handleImageLoad}
                onError={handleImageError}
                loading="lazy"
                className="template-image"
                sx={{
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: imageLoaded ? 1 : 0,
                  // Mobile image optimization
                  ...(isMobile && {
                    maxWidth: '100%',
                    height: 'auto',
                    aspectRatio: '16/9'
                  })
                }}
              />
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.palette.grey[200],
                  color: theme.palette.grey[500]
                }}
              >
                {template.is_video ? (
                  <VideoIcon sx={{ fontSize: 48 }} />
                ) : (
                  <ImageIcon sx={{ fontSize: 48 }} />
                )}
              </Box>
            )}
            
            {!imageLoaded && !imageError && (
              <Skeleton 
                variant="rectangular" 
                sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '100%' 
                }} 
              />
            )}
          </>
        ) : (
          <Skeleton variant="rectangular" height="100%" />
        )}

        {/* Template Type Badge */}
        <Chip
          size="small"
          label={template.is_video ? 'Video' : 'Image'}
          color={template.is_video ? 'primary' : 'secondary'}
          icon={template.is_video ? <VideoIcon /> : <ImageIcon />}
          className="template-badge"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            fontWeight: 600,
            fontSize: '0.75rem',
            border: `1px solid ${template.is_video ? theme.palette.primary.main : theme.palette.secondary.main}20`,
            transition: 'all 0.2s ease-in-out',
            '& .MuiChip-icon': {
              fontSize: '0.875rem'
            },
            '& .MuiChip-label': {
              paddingLeft: '6px',
              paddingRight: '8px',
            }
          }}
          aria-label={`${template.is_video ? 'Video' : 'Image'} template`}
        />
      </Box>

      {/* Template Content */}
      <CardContent sx={{ 
        flexGrow: 1, 
        pb: 1,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}>
        <Typography 
          id={`template-title-${template.id}`}
          variant="h6" 
          component="h3" 
          sx={{
            fontSize: isMobile ? '1rem' : '1.1rem',
            lineHeight: 1.3,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: theme.palette.text.primary,
            mb: 0.5,
          }}
        >
          {template.name}
        </Typography>
        
        {template.category && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              fontSize: '0.8rem',
              fontWeight: 500,
              textTransform: 'capitalize',
            }}
          >
            {template.category}
          </Typography>
        )}

        <Typography 
          id={`template-desc-${template.id}`}
          variant="caption" 
          color="text.secondary"
          sx={{ 
            display: 'block',
            fontSize: '0.75rem',
            mt: 'auto',
            opacity: 0.8,
          }}
        >
          {template.is_video ? 'Video template' : 'Image template'} • {' '}
          {template.updated_at 
            ? `Updated ${new Date(template.updated_at).toLocaleDateString()}`
            : `Created ${new Date(template.created_at || Date.now()).toLocaleDateString()}`
          }
          {template.category && ` • ${template.category}`}
        </Typography>
      </CardContent>

      {/* Template Actions */}
      <CardActions 
        className="template-actions"
        sx={{ 
          justifyContent: 'space-between',
          pt: 0,
          px: 2,
          pb: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
          opacity: 0.9,
          transform: 'translateY(2px)',
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, marginTop: 1 }}>
          <Tooltip title="Edit Template" arrow>
            <Button
              size={isMobile ? "medium" : "small"}
              component={Link}
              to={`/editor/${template.id}`}
              aria-label={`Edit ${template.name} template`}
              sx={{
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main + '10',
                border: `1px solid ${theme.palette.primary.main}20`,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main + '20',
                  transform: 'scale(1.05)',
                }
              }}
            >
              <EditIcon fontSize={isMobile ? "medium" : "small"} />
                Edit
            </Button>
          </Tooltip>
          
          <Tooltip title="Use Template" arrow>
            <Button
              size={isMobile ? "medium" : "small"}
              component={Link}
              to={`/template/create/${template.id}`}
              aria-label={`Use ${template.name} template to create post`}
              sx={{
                color: theme.palette.secondary.main,
                backgroundColor: theme.palette.secondary.main + '10',
                border: `1px solid ${theme.palette.secondary.main}20`,
                '&:hover': {
                  backgroundColor: theme.palette.secondary.main + '20',
                  transform: 'scale(1.05)',
                }
              }}
            >
              <PostIcon fontSize={isMobile ? "medium" : "small"} />
                Create
            </Button>
          </Tooltip>
        </Box>

        {/* Performance Score (if available) */}
        {template.performance_score && process.env.NODE_ENV === 'development' && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              fontWeight: 500,
            }}
          >
            {Math.round(template.performance_score)}%
          </Typography>
        )}
      </CardActions>
    </Card>
  );
};


export default TemplateCard;
